import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data/DataTable";
import { StatusBadge } from "@/components/data/StatusBadge";
import { RelTime } from "@/components/data/RelTime";
import { useErrors, paginatedItems } from "@/lib/queries";
import { fmtNumber, truncate } from "@/lib/format";
import type { ErrorRow } from "@/types";
import { Badge } from "@/components/ui/badge";

const SINCE = [
  { v: "1h", label: "1 hour" },
  { v: "24h", label: "24 hours" },
  { v: "7d", label: "7 days" },
  { v: "30d", label: "30 days" },
  { v: "all", label: "All time" },
];

const PAGE = 50;

export function ErrorsPage() {
  const [params, setParams] = useSearchParams();
  // Text inputs are drafts until Apply (or Enter); the query only sees the
  // applied values, and applying any filter goes back to page 1.
  const [pathInput, setPathInput] = useState(params.get("path") ?? "");
  const [userInput, setUserInput] = useState(params.get("user") ?? "");
  const [path, setPath] = useState(pathInput);
  const [user, setUser] = useState(userInput);
  const [since, setSince] = useState(params.get("since") ?? "24h");
  const [page, setPage] = useState(0);

  const queryParams = useMemo(
    () => ({
      limit: PAGE,
      offset: page * PAGE,
      path: path.trim() || undefined,
      user_id: user.trim() || undefined,
      since: since === "all" ? undefined : since,
    }),
    [path, user, since, page]
  );

  const apply = (nextSince = since) => {
    const next = new URLSearchParams();
    if (pathInput.trim()) next.set("path", pathInput.trim());
    if (userInput.trim()) next.set("user", userInput.trim());
    if (nextSince && nextSince !== "24h") next.set("since", nextSince);
    setParams(next, { replace: true });
    setPath(pathInput);
    setUser(userInput);
    setSince(nextSince);
    setPage(0);
  };

  const q = useErrors(queryParams);
  const rows = paginatedItems<ErrorRow>(q.data);
  const total = q.data?.total ?? rows.length;

  // Aggregations on visible page
  const topPaths = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const k = r.path ?? "—";
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [rows]);

  const cols: Column<ErrorRow>[] = [
    { key: "time", header: "Time", cell: (r) => <span className="text-xs text-muted-foreground"><RelTime value={r.created_at} /></span>, width: 130 },
    { key: "status", header: "Status", cell: (r) => <StatusBadge status={r.status} />, width: 80 },
    { key: "method", header: "Method", cell: (r) => <span className="font-mono text-xs">{r.method ?? "—"}</span>, width: 70 },
    { key: "path", header: "Path", cell: (r) => <span className="font-mono text-xs">{r.path ?? "—"}</span> },
    {
      key: "user",
      header: "User",
      cell: (r) =>
        r.user_id ? (
          <Link to={`/users/${r.user_id}`} className="font-mono text-xs text-sky-300 hover:underline">
            {r.user_email ?? r.user_id.slice(0, 8)}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
      width: 200,
    },
    { key: "msg", header: "Message", cell: (r) => <span className="text-xs text-rose-200/80">{truncate(r.message ?? "", 100)}</span> },
  ];

  return (
    <>
      <PageHeader title="Errors" description="Server-side errors logged from the API." />

      <Card className="mb-4 p-3">
        <div className="grid gap-3 sm:grid-cols-[2fr,2fr,1fr,auto]">
          <Input
            aria-label="Filter by path"
            placeholder="Filter by path (e.g. /api/expenses)"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply();
            }}
            className="font-mono"
          />
          <Input
            aria-label="Filter by user id"
            placeholder="Filter by user id"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") apply();
            }}
            className="font-mono"
          />
          <Select value={since} onValueChange={(v) => apply(v)}>
            <SelectTrigger aria-label="Time window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE.map((s) => (
                <SelectItem key={s.v} value={s.v}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={() => apply()}
            className="rounded-md bg-foreground px-3 py-1.5 text-sm font-medium text-background hover:bg-foreground/90"
          >
            Apply
          </button>
        </div>
      </Card>

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>In window</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold tabular-nums">{q.isError ? "—" : fmtNumber(total)}</div>
            <p className="mt-1 text-xs text-muted-foreground">Errors matching current filter</p>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top paths (this page)</CardTitle>
          </CardHeader>
          <CardContent>
            {topPaths.length === 0 ? (
              <p className="text-xs text-muted-foreground">—</p>
            ) : (
              <ul className="space-y-1.5">
                {topPaths.map(([p, c]) => (
                  <li key={p} className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-mono text-foreground/80">{p}</span>
                    <Badge variant="error" className="tabular-nums">{c}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={cols}
        rows={rows}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.id}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE}
        totalRows={total}
        expandable={(r) => (
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-border/40 bg-background/40 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
{r.stack ?? r.message ?? "no stack"}
          </pre>
        )}
        emptyTitle="No errors"
        emptyDescription="Try widening the time window."
      />
    </>
  );
}
